import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { CONTENT_TYPE_PDF } from '@shared/constants/files';
import { FileHandlerBaseService } from '@shared/services/file-handler/file-handler-base.service';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import {
  PdfFile,
  ProductComparePdfHandlerService,
} from './product-compare-pdf-handler.service';
import { ModalController, Platform } from '@ionic/angular';
import { ModalService } from '../../shared/services/modal/modal.service';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { ImageConverterHelpersService } from '../../shared/helpers/image-converter.helpers.service';
import { ServiceHandlerService } from '../../shared/services/service-handler.service';
import { SentenceCasePipe } from '../../shared/pipes/sentence-case.pipe';
import { TranslateService } from '@ngx-translate/core';
import { ProductDetailViewModel } from '../../product-detail/models/product-detail-view-model';
import { ShareViaEmailModalComponent } from '@shared/components/share-via-email-modal/share-via-email-modal.component';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import {
  ShareEventActions,
  ShareProductCompare,
  ShareProductCompareEmailInformation,
} from '@usf/file-share-types';
import { take } from 'rxjs';
import { ProductCompareDownloadService } from './product-compare-download.service';
import {
  SHARE_PRODUCT_COMPARE_EMAIL_ACTIONS,
  TRACE_CONTEXT,
} from '@usf/ngrx-file-share';
import { ActionsSubject, Store } from '@ngrx/store';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { ofType } from '@ngrx/effects';
import { ToastService } from '@shared/services/toast/toast.service';
import { AlertAnalyticsService } from '@usf/ngrx-alerts';

@Injectable({
  providedIn: 'root',
})
export class ProductCompareShareHandlerService extends ProductComparePdfHandlerService {
  constructor(
    translateService: TranslateService,
    sentenceCasePipe: SentenceCasePipe,
    serviceHandler: ServiceHandlerService,
    imageConverterService: ImageConverterHelpersService,
    platform: Platform,
    loadingSpinnerService: LoadingSpinnerService,
    modalController: ModalController,
    modalService: ModalService,
    platformService: PlatformService,
    private readonly fileHandlerBaseService: FileHandlerBaseService,
    private readonly panAppState: PanAppState,
    private readonly productCompareDownloadService: ProductCompareDownloadService,
    private readonly store: Store,
    private readonly actionSubject: ActionsSubject,
    private readonly toastService: ToastService,
    private readonly alertAnalyticsService: AlertAnalyticsService,
  ) {
    super(
      platform,
      loadingSpinnerService,
      translateService,
      sentenceCasePipe,
      serviceHandler,
      imageConverterService,
      modalController,
      modalService,
      platformService,
    );
  }

  protected override getRootPageParams(): {
    title: string;
    confirmButton: string;
  } {
    return {
      title: 'i18n.search.searchCompare.shareModalTitle',
      confirmButton: 'i18n.common.share',
    };
  }

  protected override generateFileName(
    productDetails: ProductDetailViewModel[],
  ): string {
    const now = new Date();
    const customerNumber =
      productDetails[0]?.selectedCustomer?.customerNumber ?? '';
    const dateString = formatDate(now, 'MMddYYYY', 'en-US');
    const timeString = formatDate(now, 'HHmmss', 'en-US');
    return `ProductCompare_Cust${customerNumber}_${dateString}_${timeString}`;
  }

  protected override handleNative(pdfFile: PdfFile): void {
    this.fileHandlerBaseService.shareFileNative(
      pdfFile.data,
      pdfFile.fileName,
      '.pdf',
      {
        title: pdfFile.fileName,
        dialogTitle: pdfFile.fileName,
      },
    );
  }

  protected override handleNonNative(pdfFile: PdfFile): void {
    throw new Error('Method not implemented.');
  }

  protected override runAnalytics(): void {
    // throw new Error('Method not implemented.');
  }

  protected override doCleanup(): void {
    this.fileHandlerBaseService.cleanUp();
  }

  async showShareViaEmailModal(productDetails: ProductDetailViewModel[]) {
    const documentName = this.generateFileName(productDetails);

    this.modalService.setModalOptions(
      this.platformService?.platformType ===
        this.platformService?.platformEnum?.mobile,
      ModalWithNavComponent,
      {
        rootPage: ShareViaEmailModalComponent,
        rootPageParams: {
          documentName: documentName,
          shareAction: ShareEventActions.ShareProductCompare,
          isMobile:
            this.platformService?.platformType ===
            this.platformService?.platformEnum?.mobile,
        },
      },
      this.platformService?.platformType ===
        this.platformService?.platformEnum?.mobile
        ? 'share-via-email-modal-mobile'
        : 'share-via-email-modal-desktop',
      true,
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();

    this.alertAnalyticsService.trackProductCompareModalOpen();

    await modal.onDidDismiss().then((resp: any) => {
      if (
        !!resp?.data &&
        !!resp?.data?.emailData &&
        !!resp?.data?.emailString &&
        resp?.data?.emailData?.emailTo !== undefined &&
        resp?.data?.emailData?.message !== undefined &&
        resp?.data?.emailData?.sendCopy !== undefined &&
        resp?.data?.emailData?.includeProductPrices !== undefined &&
        resp?.data?.emailData?.fileFormat !== undefined
      ) {
        try {
          this.loadingSpinnerService.createSpinnerModal()?.then(async () => {
            this.panAppState.user$.pipe(take(1)).subscribe(async user => {
              if (resp?.data?.emailData?.fileFormat === 'pdf') {
                const printProductCompareModel =
                  this.getPrintProductCompareModel(
                    productDetails,
                    resp?.data?.emailData?.includeProductPrices,
                  );
                const shareProductCompareModelPDF: ShareProductCompare = {
                  shareAction: ShareEventActions.ShareProductCompare,
                  fileName: documentName + '.pdf',
                  documentHeaderTitle:
                    printProductCompareModel.documentHeaderTitle,
                  includeProductPrices:
                    resp?.data?.emailData?.includeProductPrices,
                  rowLabels: printProductCompareModel.rowLabels,
                  productCompareDetails:
                    printProductCompareModel.productCompareDetails,
                  productImageUrls: printProductCompareModel.productImageUrls,
                  productStatusPills:
                    printProductCompareModel.productStatusPills,
                  ccAddresses: resp?.data?.emailData?.emailTo?.split(','),
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                  message: resp?.data?.emailData?.message,
                  sendMeACopy: resp?.data?.emailData?.sendCopy,
                  sharedBy: user?.email,
                };

                // alerts analytics
                this.alertAnalyticsService.trackShareFileProductCompare('PDF');

                this.store.dispatch(
                  SHARE_PRODUCT_COMPARE_EMAIL_ACTIONS.shareProductCompareViaEmail(
                    {
                      data: shareProductCompareModelPDF,
                      tracking: {
                        tracing: {
                          data: {
                            traceContext:
                              TRACE_CONTEXT.shareProductCompareViaEmail,
                            isStartOfTrace: true,
                            isEndOfTrace: false,
                          },
                        },
                      },
                    },
                  ),
                );
              } else {
                const shareProductCompareModel: ShareProductCompareEmailInformation =
                  {
                    fileName: documentName + '.csv',
                    shareAction: ShareEventActions.ShareProductCompare,
                    payload:
                      await this.productCompareDownloadService.generateCsvContent(
                        productDetails,
                        resp?.data?.emailData?.includeProductPrices,
                      ),
                    ccAddresses: resp?.data?.emailData?.emailTo?.split(','),
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    message: resp?.data?.emailData?.message,
                    sendMeACopy: resp?.data?.emailData?.sendCopy,
                    sharedBy: user?.email,
                  };

                // alerts analytics
                this.alertAnalyticsService.trackShareFileProductCompare('CSV');

                this.store.dispatch(
                  SHARE_PRODUCT_COMPARE_EMAIL_ACTIONS.shareProductCompareViaEmail(
                    {
                      data: shareProductCompareModel,
                      tracking: {
                        tracing: {
                          data: {
                            traceContext:
                              TRACE_CONTEXT.shareProductCompareViaEmail,
                            isStartOfTrace: true,
                            isEndOfTrace: false,
                          },
                        },
                      },
                    },
                  ),
                );
              }

              this.actionSubject
                .pipe(
                  ofType(
                    SHARE_PRODUCT_COMPARE_EMAIL_ACTIONS.shareProductCompareViaEmailSuccess,
                  ),
                )
                .subscribe(data => {
                  this.loadingSpinnerService.dismissSpinnerModal();
                  this.toastService.presentToastMsg(
                    this.translateService.instant(
                      'i18n.shareProductCompare.successMessage',
                    ) + resp?.data?.emailString,
                    'green-toast',
                    MessageTypeEnum.success,
                    [],
                  );
                });
              this.actionSubject
                .pipe(
                  ofType(
                    SHARE_PRODUCT_COMPARE_EMAIL_ACTIONS.shareProductCompareViaEmailFail,
                  ),
                )
                .subscribe(data => {
                  this.loadingSpinnerService.dismissSpinnerModal();
                  this.toastService.presentToastMsg(
                    this.translateService.instant(
                      'i18n.shareProductCompare.failMessage',
                    ) + resp?.data?.emailString,
                    'red-toast',
                    MessageTypeEnum.error,
                    [],
                  );
                });
            });
          });
        } catch (error) {
          this.loadingSpinnerService.dismissSpinnerModal();
          console.error('Error sharing product comparison via email:', error);
        }
      }
    });
  }
}
